// The six plan-tree edit handlers every editing surface needs, bound to one
// plan setter.
//
// Both editors — the Session tab (useWorkoutCoach, over the context plan) and
// the programme session dialog (EditProgrammeSessionDialog, over a local draft)
// — wire the same pure helpers from utils/planEditing to the same
// SessionPlanDisplay props. They were written out twice, and had already
// drifted: only the Session tab guarded against dropping a node into its own
// subtree. Defining them once means a fix lands on every surface at once, and a
// new surface cannot wire all but one and silently lose it.

import { useMemo } from "react";
import {
  updateNode,
  deleteNode,
  addNode,
  moveNode,
  duplicateNode,
  wrapNodesInGroup,
  splitGroupIntoRounds,
  ungroupNode,
} from "../utils/planEditing";
import { reflowChangeovers } from "../utils/transitionInjector";

/**
 * Build the handler set around `setPlan`, a React-style state setter taking an
 * updater function. Kept separate from the hook so it can be tested directly.
 */
export const createPlanEditor = (setPlan) => {
  // Every edit ends by putting the Setups (and the hidden transitions) back
  // between the blocks as they now stand, so moving or deleting a block never
  // leaves one stranded or two stacked together.
  const edit = (change) => setPlan((prev) => reflowChangeovers(change(prev)));

  const updatePlanNode = (indexPath, updates) => {
    edit((prev) => updateNode(prev, indexPath, updates));
  };

  const deletePlanNode = (indexPath) => {
    edit((prev) => deleteNode(prev, indexPath));
  };

  const addPlanNode = (parentIndexPath, newNode, insertIndex) => {
    edit((prev) => addNode(prev, parentIndexPath, newNode, insertIndex));
  };

  const movePlanNode = (fromIndexPath, toParentIndexPath, toIndex) => {
    // Cheap up-front rejection so an invalid move never triggers a state
    // update (moveNode itself is guarded too, but this keeps a no-op move from
    // churning the tree). A node can never be dropped inside its own subtree.
    if (!Array.isArray(fromIndexPath) || fromIndexPath.length === 0) return;
    if (!Array.isArray(toParentIndexPath)) return;
    if (toParentIndexPath.length >= fromIndexPath.length) {
      const isDescendant = fromIndexPath.every(
        (seg, i) => toParentIndexPath[i] === seg,
      );
      if (isDescendant) return;
    }
    edit((prev) => moveNode(prev, fromIndexPath, toParentIndexPath, toIndex));
  };

  const duplicatePlanNode = (indexPath) => {
    edit((prev) => duplicateNode(prev, indexPath));
  };

  // Collect selected sibling rows into a new block, so a flat session can be
  // organised after the fact instead of only at authoring time.
  const wrapPlanNodes = (indexPaths, groupNode) => {
    if (!Array.isArray(indexPaths) || indexPaths.length === 0) return;
    edit((prev) => wrapNodesInGroup(prev, indexPaths, groupNode));
  };

  // Unroll a repeating block into one named block per round.
  const splitPlanNode = (indexPath, names) => {
    edit((prev) => splitGroupIntoRounds(prev, indexPath, names));
  };

  // Remove a block that runs once, keeping its steps in place.
  const ungroupPlanNode = (indexPath) => {
    edit((prev) => ungroupNode(prev, indexPath));
  };

  return {
    updatePlanNode,
    deletePlanNode,
    addPlanNode,
    movePlanNode,
    duplicatePlanNode,
    wrapPlanNodes,
    splitPlanNode,
    ungroupPlanNode,
    // Ready to spread onto <SessionPlanDisplay editable …>. Callers that also
    // need a handler on its own (renaming the session calls updatePlanNode with
    // an empty path) still take it from above.
    planDisplayProps: {
      onEditNode: updatePlanNode,
      onDeleteNode: deletePlanNode,
      onAddNode: addPlanNode,
      onMoveNode: movePlanNode,
      onDuplicateNode: duplicatePlanNode,
      onWrapNodes: wrapPlanNodes,
      onSplitNode: splitPlanNode,
      onUngroupNode: ungroupPlanNode,
    },
  };
};

const usePlanEditor = (setPlan) =>
  useMemo(() => createPlanEditor(setPlan), [setPlan]);

export default usePlanEditor;
